import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import type { SearchResults } from '../api/types'
import { titleCase } from '../lib/format'
import { Icon } from './Icon'

interface Hit { group: string; icon: string; label: string; sub?: string; to: string }

/** Search where the search box is. It used to open a dialog in the middle of the
 *  screen, which covered the very page you were looking at — you'd search a name
 *  to compare it against what was behind the dialog. Results drop out of the rail
 *  instead, and the page stays put. */
export function RailSearch() {
  const [q, setQ] = useState('')
  const [results, setResults] = useState<SearchResults | null>(null)
  const [active, setActive] = useState(0)
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const nav = useNavigate()

  // ⌘K focuses the box rather than opening anything.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        input.current?.focus()
        input.current?.select()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [open])

  // Debounced; a stale response is dropped by comparing the query it was for.
  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) return setResults(null)
    const t = window.setTimeout(() => {
      api.search(term).then((r) => {
        if (input.current?.value.trim() === term) setResults(r)
      }).catch(() => setResults(null))
    }, 160)
    return () => window.clearTimeout(t)
  }, [q])

  const hits: Hit[] = useMemo(() => {
    const out: Hit[] = []
    for (const l of results?.leads ?? []) {
      out.push({ group: 'Contacts', icon: 'person', label: l.full_name ?? '—',
        sub: [l.city, titleCase(l.status)].filter(Boolean).join(' · '), to: `/contacts/c/${l.id}` })
    }
    for (const p of results?.people ?? []) {
      if (p.contact_id && results?.leads.some((l) => l.id === p.contact_id)) continue
      out.push({ group: 'Partners', icon: 'badge', label: p.name ?? '—', sub: p.company ?? undefined,
        to: p.contact_id ? `/contacts/c/${p.contact_id}` : `/partners/${encodeURIComponent(p.person_key)}` })
    }
    for (const l of results?.loans ?? []) {
      out.push({ group: 'Loans', icon: 'home_work', label: l.borrower ?? l.loan_number ?? '—',
        sub: [l.property_street, titleCase(l.status)].filter(Boolean).join(' · '),
        to: `/loans/${encodeURIComponent(l.loan_id)}` })
    }
    return out
  }, [results])

  useEffect(() => setActive(0), [hits.length])

  const goTo = (hit: Hit) => {
    setOpen(false)
    setQ('')
    setResults(null)
    nav(hit.to)
  }

  let lastGroup = ''
  return (
    <div className="railsearch-box" ref={box}>
      <div className="railsearch">
        <Icon name="search" size={18} />
        <input
          ref={input}
          className="railsearch-input"
          placeholder="Search contacts"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { setOpen(false); input.current?.blur() }
            if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(hits.length - 1, a + 1)) }
            if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)) }
            if (e.key === 'Enter' && hits[active]) goTo(hits[active])
          }}
          aria-label="Search contacts, partners and loans"
        />
        {q ? (
          <button className="icon-btn-plain" style={{ marginLeft: 0 }} aria-label="Clear search"
            onClick={() => { setQ(''); setResults(null); input.current?.focus() }}>
            <Icon name="close" size={16} />
          </button>
        ) : <span className="kbd">⌘K</span>}
      </div>

      {open && q.trim().length >= 2 ? (
        <div className="railsearch-results" role="listbox">
          {hits.length === 0 ? (
            <p className="quiet" style={{ margin: 0, padding: '10px 12px', fontSize: 12.5 }}>
              Nothing matches “{q.trim()}”.
            </p>
          ) : hits.map((hit, i) => {
            const header = hit.group !== lastGroup ? hit.group : ''
            lastGroup = hit.group
            return (
              <div key={`${hit.to}-${i}`}>
                {header ? <div className="palette-group">{header.toUpperCase()}</div> : null}
                <button className={`palette-item${i === active ? ' active' : ''}`}
                  onMouseEnter={() => setActive(i)} onClick={() => goTo(hit)} role="option" aria-selected={i === active}>
                  <Icon name={hit.icon} size={17} style={{ color: 'var(--ink-3)' }} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13 }}>{hit.label}</span>
                    {hit.sub ? <span className="hint" style={{ display: 'block' }}>{hit.sub}</span> : null}
                  </span>
                </button>
              </div>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
